import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  announcementDate,
  feedEntries,
  type Announcement,
} from "@salestrak/announcements";
import { GetDiscoveriesDocument, SetSettingsDocument } from "#app/lib/data";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import {
  DISCOVERY_DISMISSED,
  discoverySettingKey,
} from "#app/lib/use-discovery";

/** Discovery flag name for an announcement entry (setting key `discovery_feat_<key>`). */
const discoveryName = (entryKey: string) => `feat_${entryKey}`;

/**
 * The What's-New feed: catalog entries visible to the current viewer (flag-
 * gated entries follow the workspace's resolved flags), windowed to recent
 * entries via `feedEntries` (90 days / 8 max — older history lives on the
 * marketing changelog), with per-entry seen-state resolved server-side
 * through the batch `discoveries` query.
 *
 * Each entry's `announcementDate` (GA date, else publish date — the same key
 * the feed sorts by) rides along as the discovery `releasedAt` fallback, so a
 * member who joined before an entry became new to them still counts it as
 * unseen, and one who joined after does not. `unseenKeys` stays empty until
 * the server confirms — a brand-new member must never see a flash of
 * indicators (same contract as `useDiscovery`).
 *
 * `markAllSeen()` dismisses every unseen entry in one batch `setSettings`
 * round-trip, optimistically zeroing the count via the mutation's cache
 * update (rolled back automatically if the write fails).
 */
export function useAnnouncements(): {
  entries: Announcement[];
  unseenKeys: ReadonlySet<string>;
  unseenCount: number;
  markAllSeen: () => void;
} {
  const { ready, hasFlag } = useFeatureFlags();

  const entries = useMemo(
    () =>
      ready
        ? // The catalog types flag keys as the enum's string-literal union
          // (it must stay free of runtime imports); the values are the same.
          feedEntries((key) => hasFlag(key as FeatureFlagKey), new Date())
        : [],
    [ready, hasFlag],
  );

  const variables = useMemo(
    () => ({
      items: entries.map((entry) => ({
        name: discoveryName(entry.key),
        releasedAt: announcementDate(entry),
      })),
    }),
    [entries],
  );

  const { data } = useQuery(GetDiscoveriesDocument, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: entries.length === 0,
  });

  const [setSettings] = useMutation(SetSettingsDocument);

  const unseenKeys = useMemo(() => {
    const visibleNames = new Set(
      (data?.discoveries ?? [])
        .filter((d) => d.visible)
        .map((d) => d.name),
    );
    return new Set(
      entries
        .filter((entry) => visibleNames.has(discoveryName(entry.key)))
        .map((entry) => entry.key),
    );
  }, [data, entries]);

  const markAllSeen = useCallback(() => {
    if (unseenKeys.size === 0 || !data) return;
    const dismissedNames = new Set(
      [...unseenKeys].map((key) => discoveryName(key)),
    );
    const discoveries = data.discoveries;
    // One batch write persists every dismissal; the mutation's `update`
    // (fired on the optimistic response and again on the server response)
    // zeroes the unseen count, and Apollo rolls it back if the write fails —
    // so a failed persist can't leave the badge silently cleared.
    void setSettings({
      variables: {
        items: [...dismissedNames].map((name) => ({
          key: discoverySettingKey(name),
          value: DISCOVERY_DISMISSED,
        })),
      },
      optimisticResponse: { setSettings: true },
      update: (cache) => {
        cache.writeQuery({
          query: GetDiscoveriesDocument,
          variables,
          data: {
            discoveries: discoveries.map((d) =>
              dismissedNames.has(d.name) ? { ...d, visible: false } : d,
            ),
          },
        });
      },
    }).catch(() => {});
  }, [unseenKeys, data, variables, setSettings]);

  return { entries, unseenKeys, unseenCount: unseenKeys.size, markAllSeen };
}
